import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./CollapsibleSection.scss";

const SLIDE_MS = 480;

function actionKey(actions) {
  return actions.map((action) => `${action.id}:${action.busy ? "b" : ""}${action.disabled ? "d" : ""}`).join("|");
}

function overflowEntries(actions) {
  return actions.flatMap((action) => (action.menu && action.menu.length ? action.menu : [action]));
}

/**
 * Header stays visible when the section closes. Optional actions stay in
 * the header and move into a three-dot menu when they no longer fit.
 * The body slides shut and clips, so tables and charts cannot leak out.
 */
export default function CollapsibleSection({
  title,
  subtitle = "",
  meta = "",
  actions = [],
  children,
  defaultOpen = true,
  className = "",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [settled, setSettled] = useState(defaultOpen);
  const [bodyHeight, setBodyHeight] = useState(defaultOpen ? "auto" : 0);
  const [visibleCount, setVisibleCount] = useState(actions.length);
  const [menuId, setMenuId] = useState(null);
  const actionsSignature = actionKey(actions);
  const headerRef = useRef(null);
  const chevronRef = useRef(null);
  const measureRef = useRef(null);
  const menuRef = useRef(null);
  const bodyRef = useRef(null);
  const skipSlide = useRef(true);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    if (skipSlide.current) {
      skipSlide.current = false;
      setBodyHeight(open ? "auto" : 0);
      setSettled(open);
      return undefined;
    }
    if (!open) {
      setSettled(false);
      setMenuId(null);
      setBodyHeight(body.scrollHeight);
      const frame = requestAnimationFrame(() => setBodyHeight(0));
      return () => cancelAnimationFrame(frame);
    }
    setBodyHeight(body.scrollHeight);
    const timer = window.setTimeout(() => {
      setBodyHeight("auto");
      setSettled(true);
      window.dispatchEvent(new Event("resize"));
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useLayoutEffect(() => {
    const measure = () => {
      const header = headerRef.current;
      const measurer = measureRef.current;
      if (!header || !measurer || actions.length === 0) {
        setVisibleCount(actions.length);
        return;
      }
      const style = window.getComputedStyle(header);
      const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const gap = parseFloat(style.columnGap || style.gap) || 8;
      const chevronWidth = chevronRef.current ? chevronRef.current.offsetWidth : 36;
      const moreWidth = measurer.querySelector('[data-measure="more"]')?.offsetWidth || 36;
      const titleMin = 120;
      let available = header.clientWidth - pad - chevronWidth - gap - titleMin - gap;
      if (available < 0) available = 0;
      const widths = actions.map((action) => measurer.querySelector(`[data-measure="${action.id}"]`)?.offsetWidth || 0);
      let used = 0;
      let count = 0;
      for (let index = 0; index < widths.length; index += 1) {
        const itemGap = count > 0 ? 8 : 0;
        const reserve = index < widths.length - 1 ? moreWidth + 8 : 0;
        if (used + itemGap + widths[index] + reserve <= available + 1) {
          used += itemGap + widths[index];
          count += 1;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsSignature, subtitle, meta, title]);

  useEffect(() => {
    if (!menuId) return undefined;
    const close = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuId(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setMenuId(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuId]);

  const toggle = () => setOpen((value) => !value);
  const visibleActions = actions.slice(0, visibleCount);
  const overflowActions = actions.slice(visibleCount);

  const renderMenuItems = (items) => overflowEntries(items).map((item) => (
    <button
      key={item.id}
      type="button"
      role="menuitem"
      disabled={item.disabled}
      onClick={() => {
        setMenuId(null);
        if (item.onClick) item.onClick();
      }}
    >
      {item.icon ? <i className={`bi ${item.icon}`} aria-hidden="true" /> : null}
      {item.label}
    </button>
  ));

  return (
    <section
      className={`collapsible-section${open ? " is-open" : ""}${
        settled ? " is-settled" : ""
      }${menuId ? " is-menu-open" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="collapsible-section__header" ref={headerRef}>
        <button
          type="button"
          className="collapsible-section__toggle"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className="collapsible-section__text">
            <span className="collapsible-section__title">{title}</span>
            {subtitle ? <span className="collapsible-section__subtitle">{subtitle}</span> : null}
            {meta ? <span className="collapsible-section__meta">{meta}</span> : null}
          </span>
        </button>
        {actions.length > 0 && (
          <div className="collapsible-section__actions" ref={menuRef}>
            {visibleActions.map((action) => (
              <div className="collapsible-section__menu-wrap" key={action.id}>
                <button
                  type="button"
                  className="collapsible-section__action"
                  aria-label={action.label}
                  title={action.label}
                  disabled={action.disabled || action.busy}
                  aria-expanded={action.menu ? menuId === action.id : undefined}
                  aria-haspopup={action.menu ? "menu" : undefined}
                  onClick={() => {
                    if (action.menu && action.menu.length) {
                      setMenuId((current) => (current === action.id ? null : action.id));
                      return;
                    }
                    if (action.onClick) action.onClick();
                  }}
                >
                  <i
                    className={`bi ${action.busy ? "bi-arrow-repeat collapsible-section__spin" : action.icon || "bi-circle"}`}
                    aria-hidden="true"
                  />
                </button>
                {action.menu && menuId === action.id && (
                  <div className="collapsible-section__menu" role="menu">
                    {renderMenuItems([action])}
                  </div>
                )}
              </div>
            ))}
            {overflowActions.length > 0 && (
              <div className="collapsible-section__menu-wrap">
                <button
                  type="button"
                  className="collapsible-section__action"
                  aria-label="More options"
                  title="More options"
                  aria-expanded={menuId === "more"}
                  aria-haspopup="menu"
                  onClick={() => setMenuId((current) => (current === "more" ? null : "more"))}
                >
                  <i className="bi bi-three-dots" aria-hidden="true" />
                </button>
                {menuId === "more" && (
                  <div className="collapsible-section__menu" role="menu">
                    {renderMenuItems(overflowActions)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          className="collapsible-section__chevron"
          aria-label={open ? "Collapse section" : "Expand section"}
          ref={chevronRef}
          onClick={toggle}
        >
          <i className="bi bi-chevron-down" aria-hidden="true" />
        </button>
        <div className="collapsible-section__measure" ref={measureRef} aria-hidden="true">
          {actions.map((action) => (
            <span key={action.id} data-measure={action.id} className="collapsible-section__action">
              <i className={`bi ${action.icon || "bi-circle"}`} aria-hidden="true" />
            </span>
          ))}
          <span data-measure="more" className="collapsible-section__action">
            <i className="bi bi-three-dots" aria-hidden="true" />
          </span>
        </div>
      </div>
      <div
        className="collapsible-section__body"
        ref={bodyRef}
        style={{ height: bodyHeight === "auto" ? "auto" : `${bodyHeight}px` }}
        aria-hidden={!open}
      >
        <div className="collapsible-section__body-inner">{children}</div>
      </div>
    </section>
  );
}
